import { useLanguage } from '@vocab/react';
import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ROUTE_SUCCESS } from 'src/app/shared/constants/routes';
import {
  FEATURE_CART_AND_PAY_AND_POST_V2,
  FEATURE_TEST_SHOPPING_CART,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import { UserIntentType } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useShoppingCartForAdPosting = () => {
  const { jobLocationId, jobTitle, subClassificationId, draftId } = useSelector(
    (state: AppState) => ({
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      subClassificationId: state.session.seekSubClassificationId,
      draftId: state.session.draftId,
    }),
    shallowEqual,
  );

  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);
  const isTestingCartEnabled = useFeatureToggleData(FEATURE_TEST_SHOPPING_CART);

  const { pathname } = useLocation();
  const isSuccessPage = pathname.includes(ROUTE_SUCCESS);
  const { locale, language } = useLanguage();

  const { setCartId, updateInputData } = useShoppingCart();

  useEffect(() => {
    if (isSuccessPage) {
      return;
    }

    if ((isCartEnabled || isTestingCartEnabled) && draftId) {
      try {
        setCartId(draftId);
      } catch (error) {
        log.error('useShoppingCartForAdPosting | setCartId error', {
          error,
        });
      }
    }
  }, [draftId, isCartEnabled, isSuccessPage, isTestingCartEnabled, setCartId]);

  useEffect(() => {
    if (isSuccessPage) {
      return;
    }

    if (isCartEnabled || isTestingCartEnabled) {
      const localeForCreateProductSet = locale || language;
      const isCreateProductSetDataReady =
        draftId &&
        jobLocationId &&
        jobTitle &&
        localeForCreateProductSet &&
        subClassificationId;

      if (isCreateProductSetDataReady) {
        try {
          updateInputData({
            draftId,
            locale: localeForCreateProductSet,
            locationId: jobLocationId,
            opportunityId: draftId,
            roleTitle: jobTitle,
            subClassificationId,
            userIntent: isCartEnabled
              ? UserIntentType.PURCHASE
              : UserIntentType.PREVIEW,
          });
        } catch (error) {
          log.error('useShoppingCartForAdPosting | updateInputData error', {
            error,
          });
        }
      }
    }
  }, [
    draftId,
    isCartEnabled,
    isSuccessPage,
    isTestingCartEnabled,
    jobLocationId,
    jobTitle,
    language,
    locale,
    subClassificationId,
    updateInputData,
  ]);
};
